import { toast } from "sonner";
import { logger } from "@/lib/logger";

/** Safely read a string `message` from an unknown error value. */
function errorMessage(error: unknown): string | undefined {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

/** Safely read a string `code` from an unknown error value (e.g. Postgres error codes). */
function errorCode(error: unknown): string | undefined {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

// Common error handler
export const handleApiError = (error: unknown, defaultMessage: string) => {
  logger.error("API Error", error);
  const message = errorMessage(error) || defaultMessage;
  toast.error(message);
  throw new Error(message);
};

/**
 * Standardized error handling for Supabase operations
 * Throws errors with proper formatting for consistent error propagation
 */
export function handleSupabaseError(error: unknown, operation: string = "operation") {
  const code = errorCode(error);
  const message = errorMessage(error);
  if (code === "42501") {
    // Row Level Security violation
    throw new Error(`Access denied: You don't have permission to perform this ${operation}.`);
  } else if (code === "23505") {
    // Unique constraint violation
    throw new Error("A record with these details already exists.");
  } else if (code === "23503") {
    // Foreign key constraint violation
    throw new Error("Referenced record not found.");
  } else if (message) {
    throw new Error(message);
  } else {
    throw new Error(`Failed to ${operation}.`);
  }
}
